/**
 * CR500: DealCardSpec v2 Type Definitions
 * 
 * Core types for the block-based card composition system.
 * Keep types tight but forward-compatible.
 */

// ================================================================
// BLOCK TYPES
// ================================================================

/**
 * All supported block types in the v2 card system.
 * New blocks must be added here and in the registry.
 */
export type DealCardBlockType =
  | "header"
  | "ribbon"
  | "starburst"
  | "meta_pills"
  | "headline"
  | "promo_badges"
  | "details_pills"
  | "value_box"
  | "spend_box"
  | "reward_box"
  | "eligibility_box"
  | "merchant_category_chips"
  | "dates"
  | "cta_button"
  | "feedback"
  | "details_cta" // CR309: Details trigger button
  | "cta_details" // CR323: Legacy alias for details_cta (backwards compat)
  | "confidence_indicator"
  | "debug_strip"
  // Details surface blocks
  | "terms"
  | "fine_print"
  | "source"
  | "evidence"
  | "validator"
  | "actions"
  // Phase 5A: Back-face blocks
  | "back_header"
  | "back_nav"
  | "details_actions"
  // CR321: Eligibility block
  | "eligibility"
  // CR332: Flyer snippet block
  | "flyer_snippet"
  // CR335: Flyer snippet details block (card back, admin-only)
  | "flyer_snippet_details"
  // CR481: Card Snippet (configurable snippet with eligibility + fallback)
  | "card_snippet";

/**
 * Block specification - defines a renderable unit within a surface.
 */
export interface DealCardBlockSpec {
  /** Stable identifier for this block instance (e.g., "headline") */
  id: string;
  /** Registry key determining which component renders this block */
  type: DealCardBlockType;
  /** Whether this block should be rendered */
  enabled: boolean;
  /** Sort order within the surface (lower = earlier) */
  order: number;
  /** Optional block-specific configuration */
  config?: Record<string, unknown>;
}

// ================================================================
// SURFACE TYPES
// ================================================================

/**
 * A surface is a named collection of blocks (e.g., primary card face, details panel).
 */
export interface DealCardSurface {
  blocks: DealCardBlockSpec[];
}

/**
 * Standard surface keys.
 */
export type DealCardSurfaceKey = "primary" | "details";

// ================================================================
// SPEC TYPES
// ================================================================

/**
 * The full DealCardSpec v2 schema.
 * This is what gets stored in deal_card_template_variants.template_json.
 */
export interface DealCardSpecV2 {
  /** Schema version - always 2 for v2 specs */
  schemaVersion: 2;
  /** Block catalog version for compatibility checks */
  blockCatalogVersion: number;
  
  /** Surfaces containing blocks to render */
  surfaces: {
    primary: DealCardSurface;
    details: DealCardSurface;
  };
  
  /** Default settings for the spec */
  defaults?: DealCardSpecDefaults;
}

/**
 * Default settings that can be defined at the spec level.
 */
export interface DealCardSpecDefaults {
  /** Header rendering variant */
  headerVariant?: "standard" | "compact";
  /** Value box rendering variant */
  valueBoxVariant?: "standard" | "minimal";
  /** How secondary details are presented */
  secondaryDetailsMode?: "flip" | "modal" | "panel" | "inline";
}

// ================================================================
// OVERRIDE TYPES
// ================================================================

/**
 * Per-deal-type overrides stored in deal_card_templates.overrides_json.
 * These modify the base variant template for specific deal types.
 */
export interface DealCardOverridesV2 {
  /** Schema version - always 2 for v2 overrides */
  schemaVersion: 2;
  /** Reference to the variant this override applies to */
  variantKey: string;
  /** Label overrides for deal-type specific copy */
  labels?: Record<string, string>;
  /** Per-surface block toggle overrides */
  surfaceOverrides?: {
    primary?: SurfaceOverrides;
    details?: SurfaceOverrides;
  };
  /** Style overrides (colors, spacing, etc.) */
  styleOverrides?: Record<string, unknown>;
}

/**
 * Overrides applicable to a single surface.
 */
export interface SurfaceOverrides {
  /** Toggle individual blocks on/off (key = block id) */
  blockToggles?: Record<string, boolean>;
  /** Per-block config overrides (key = block id) */
  blockConfigs?: Record<string, Record<string, unknown>>;
  /** CR309: Custom block order (array of block IDs) - if omitted, use default order */
  blockOrder?: string[];
}

// ================================================================
// RESOLVED SPEC TYPES
// ================================================================

/**
 * The fully resolved spec ready for rendering.
 * This is the output of the resolver after merging variant + overrides.
 */
export interface ResolvedDealCardSpecV2 extends DealCardSpecV2 {
  /** Resolution metadata */
  resolved: {
    /** The variant key that was used */
    variantKey: string;
    /** The deal type these overrides apply to */
    dealTypeKey: string;
    /** Merged labels from variant and overrides */
    labels: Record<string, string>;
    /** Merged style overrides */
    styleOverrides: Record<string, unknown>;
    /** Whether this used fallback values */
    usedFallback: boolean;
    /** Source tracking for debugging */
    sources: {
      variant: "db" | "fallback";
      overrides: "db" | "fallback";
    };
    /** CR325: Component configurations from template_json.components */
    components?: Record<string, unknown>;
  };
}

// ================================================================
// RENDER CONTEXT
// ================================================================

/**
 * Context passed to block renderers.
 */
export interface DealCardRenderContext {
  /** Is this an admin/preview context? */
  isAdmin: boolean;
  /** Surface being rendered (for context-aware blocks) */
  surface: DealCardSurfaceKey;
  /** Resolved labels from the spec */
  labels: Record<string, string>;
  /** Style overrides from the spec */
  styleOverrides: Record<string, unknown>;
  /** Global theme tokens (if available) */
  themeTokens?: Record<string, unknown>;
  /** Flip-back handler for back_nav block (flip mode only) */
  onFlipBack?: () => void;
  /** CR308/CR325: Component configurations (starburst, ribbon, etc.) */
  components?: Record<string, unknown>;
  /** CR451: Flyer interaction config from Layout Studio */
  flyerInteraction?: {
    interaction?: "none" | "modal";
    allowBackdropClose?: boolean;
    modalTitle?: string;
  };
}

// ================================================================
// BLOCK PROPS
// ================================================================

/**
 * Standard props passed to all block components.
 */
export interface DealCardBlockProps<TDeal = unknown> {
  /** The deal data to render */
  deal: TDeal;
  /** The block specification */
  block: DealCardBlockSpec;
  /** Render context */
  context: DealCardRenderContext;
}

// ================================================================
// DB ROW TYPES (for Supabase queries)
// ================================================================

/**
 * Raw row from deal_card_template_variants table.
 */
export interface DealCardTemplateVariantRow {
  variant_key: string;
  label: string;
  description: string | null;
  template_json: unknown; // JSON from DB
  created_at: string;
  updated_at: string;
  created_by: string | null;
  updated_by: string | null;
}

/**
 * Raw row from deal_card_templates table (v2 columns).
 */
export interface DealCardTemplateRowV2 {
  deal_type_key: string;
  variant_key: string | null;
  overrides_json: unknown | null; // JSON from DB
  legacy_template_json: unknown | null;
  template_json: unknown; // v1 template (kept for backward compat)
  updated_by: string | null;
  updated_at: string;
}

// ================================================================
// TYPE GUARDS
// ================================================================

/**
 * Check if a value is a valid DealCardSpecV2.
 */
export function isDealCardSpecV2(value: unknown): value is DealCardSpecV2 {
  if (!value || typeof value !== "object") return false;
  const obj = value as Record<string, unknown>;
  return (
    obj.schemaVersion === 2 &&
    typeof obj.blockCatalogVersion === "number" &&
    obj.surfaces != null &&
    typeof obj.surfaces === "object"
  );
}

/**
 * Check if a value is a valid DealCardOverridesV2.
 */
export function isDealCardOverridesV2(value: unknown): value is DealCardOverridesV2 {
  if (!value || typeof value !== "object") return false;
  const obj = value as Record<string, unknown>;
  return obj.schemaVersion === 2 && typeof obj.variantKey === "string";
}

/**
 * Check if a block type is valid.
 */
export function isValidBlockType(type: string): type is DealCardBlockType {
  const validTypes: DealCardBlockType[] = [
    "header", "ribbon", "starburst", "meta_pills", "headline", "promo_badges",
    "details_pills", "value_box", "spend_box", "reward_box", "eligibility_box",
    "merchant_category_chips", "dates", "cta_button", "feedback", "details_cta", "cta_details",
    "confidence_indicator", "debug_strip",
    "terms", "fine_print", "source", "evidence", "validator", "actions",
    // Phase 5A: Back-face blocks
    "back_header", "back_nav", "details_actions",
    // CR321: Eligibility block
    "eligibility",
    // CR332: Flyer snippet block
    "flyer_snippet",
    // CR335: Flyer snippet details block
    "flyer_snippet_details",
    // CR481: Card Snippet
    "card_snippet",
  ];
  return validTypes.includes(type as DealCardBlockType);
}
